import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Pagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  AccountBalanceWallet as GiftCardIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';

interface GiftCard {
  _id: string;
  cardNumber: string;
  type: 'Physical' | 'Digital' | 'Promotional';
  status: 'Active' | 'Inactive' | 'Expired' | 'Suspended' | 'Depleted';
  initialValue: number;
  currentBalance: number;
  purchasedBy?: {
    name: string;
    email: string;
  };
  recipient?: {
    name: string;
    email: string;
  };
  expirationDate: string;
  createdAt: string;
  lastUsedDate?: string;
}

interface GiftCardAnalytics {
  totalIssued: number;
  totalValue: number;
  totalRedeemed: number;
  outstandingBalance: number;
  averageValue: number;
  redemptionRate: number;
  statusBreakdown: Array<{
    _id: string;
    count: number;
    totalValue: number;
  }>;
  expiringCardsCount: number;
}

const GiftCardsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [analytics, setAnalytics] = useState<GiftCardAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState<GiftCard | null>(null);

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    startDate: null as any,
    endDate: null as any
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });

  // Create form
  const [createForm, setCreateForm] = useState({
    amount: 50,
    quantity: 1,
    type: 'Digital' as 'Physical' | 'Digital' | 'Promotional',
    expirationMonths: 24,
    recipient: {
      name: '',
      email: ''
    },
    notes: ''
  });

  useEffect(() => {
    fetchGiftCards();
    fetchAnalytics();
  }, [filters, pagination.page]);

  const fetchGiftCards = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        startDate: filters.startDate ? dayjs(filters.startDate).format('YYYY-MM-DD') : undefined,
        endDate: filters.endDate ? dayjs(filters.endDate).format('YYYY-MM-DD') : undefined
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (!params[key as keyof typeof params]) {
          delete params[key as keyof typeof params];
        }
      });

      const response = await api.get('/gift-cards/admin/all', { params });
      setGiftCards(response.data.giftCards);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch gift cards');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = {
        startDate: filters.startDate ? dayjs(filters.startDate).format('YYYY-MM-DD') : undefined,
        endDate: filters.endDate ? dayjs(filters.endDate).format('YYYY-MM-DD') : undefined
      };

      const response = await api.get('/gift-cards/admin/analytics', { params });
      setAnalytics(response.data);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const createGiftCards = async () => {
    try {
      setLoading(true);
      const payload = {
        amount: createForm.amount,
        quantity: createForm.quantity,
        type: createForm.type,
        expirationDate: createForm.expirationMonths > 0 
          ? dayjs().add(createForm.expirationMonths, 'month').toDate()
          : undefined,
        recipient: createForm.recipient.name ? createForm.recipient : undefined,
        notes: createForm.notes || undefined
      };

      const response = await api.post('/gift-cards/admin/create', payload);
      setSuccessMessage(response.data.message);
      setCreateDialog(false);
      resetCreateForm();
      fetchGiftCards();
      fetchAnalytics();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create gift cards');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      amount: 50,
      quantity: 1,
      type: 'Digital',
      expirationMonths: 24,
      recipient: { name: '', email: '' },
      notes: ''
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'warning';
      case 'Expired': return 'error';
      case 'Suspended': return 'error';
      case 'Depleted': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      type: '',
      startDate: null,
      endDate: null
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const AnalyticsCards = () => {
    if (!analytics) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Issued
                  </Typography>
                  <Typography variant="h4">
                    {analytics.totalIssued}
                  </Typography>
                </Box>
                <GiftCardIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Value
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(analytics.totalValue)}
                  </Typography>
                </Box>
                <AnalyticsIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Redeemed
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(analytics.totalRedeemed)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.redemptionRate}% rate
                  </Typography>
                </Box>
                <AnalyticsIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Outstanding Balance
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(analytics.outstandingBalance)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.expiringCardsCount} expiring soon
                  </Typography>
                </Box>
                <AnalyticsIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gift Card Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          Create Gift Cards
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Analytics" />
        <Tab label="Gift Cards" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <AnalyticsCards />
          
          {analytics && analytics.statusBreakdown.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status Breakdown
                </Typography>
                <Grid container spacing={2}>
                  {analytics.statusBreakdown.map((status) => (
                    <Grid item xs={12} sm={6} md={3} key={status._id}>
                      <Box textAlign="center" p={2}>
                        <Chip
                          label={status._id}
                          color={getStatusColor(status._id) as any}
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="h6">{status.count}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatCurrency(status.totalValue)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search cards..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Expired">Expired</MenuItem>
                    <MenuItem value="Suspended">Suspended</MenuItem>
                    <MenuItem value="Depleted">Depleted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                    label="Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Physical">Physical</MenuItem>
                    <MenuItem value="Digital">Digital</MenuItem>
                    <MenuItem value="Promotional">Promotional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button variant="outlined" onClick={clearFilters} fullWidth>
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Gift Cards Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Card Number</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Initial Value</TableCell>
                  <TableCell align="right">Current Balance</TableCell>
                  <TableCell>Purchased By</TableCell>
                  <TableCell>Expiration</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : giftCards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No gift cards found
                    </TableCell>
                  </TableRow>
                ) : (
                  giftCards.map((card) => (
                    <TableRow key={card._id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          ****{card.cardNumber.slice(-4)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={card.type} variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={card.status}
                          color={getStatusColor(card.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(card.initialValue)}</TableCell>
                      <TableCell align="right">{formatCurrency(card.currentBalance)}</TableCell>
                      <TableCell>
                        {card.purchasedBy ? (
                          <Box>
                            <Typography variant="body2">{card.purchasedBy.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {card.purchasedBy.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Walk-in
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(card.expirationDate)}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedCard(card);
                              setViewDialog(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(_, page) => setPagination(prev => ({ ...prev, page }))}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Create Gift Cards Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Gift Cards</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={createForm.amount}
                onChange={(e) => setCreateForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={createForm.quantity}
                onChange={(e) => setCreateForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={createForm.type}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value as any }))}
                  label="Type"
                >
                  <MenuItem value="Digital">Digital</MenuItem>
                  <MenuItem value="Physical">Physical</MenuItem>
                  <MenuItem value="Promotional">Promotional</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expiration (Months)"
                type="number"
                value={createForm.expirationMonths}
                onChange={(e) => setCreateForm(prev => ({ ...prev, expirationMonths: parseInt(e.target.value) || 0 }))}
                helperText="0 = No expiration"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Recipient Name"
                value={createForm.recipient.name}
                onChange={(e) => setCreateForm(prev => ({ 
                  ...prev, 
                  recipient: { ...prev.recipient, name: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Recipient Email"
                type="email"
                value={createForm.recipient.email}
                onChange={(e) => setCreateForm(prev => ({ 
                  ...prev, 
                  recipient: { ...prev.recipient, email: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={createForm.notes}
                onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={createGiftCards}
            variant="contained"
            disabled={loading || createForm.amount <= 0}
          >
            {loading ? 'Creating...' : `Create ${createForm.quantity} Card(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Gift Card Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gift Card Details</DialogTitle>
        <DialogContent>
          {selectedCard && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Card Information
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Card Number</Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {selectedCard.cardNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={selectedCard.status}
                  color={getStatusColor(selectedCard.status) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Type</Typography>
                <Typography variant="body1">{selectedCard.type}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Created</Typography>
                <Typography variant="body1">{formatDate(selectedCard.createdAt)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Initial Value</Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(selectedCard.initialValue)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Current Balance</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(selectedCard.currentBalance)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Expiration Date</Typography>
                <Typography variant="body1">{formatDate(selectedCard.expirationDate)}</Typography>
              </Grid>
              {selectedCard.lastUsedDate && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Last Used</Typography>
                  <Typography variant="body1">{formatDate(selectedCard.lastUsedDate)}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GiftCardsPage;